import { useEffect, useMemo, useState } from "react";
import {
  getMetricsMinWidth,
  getPlayerMetricNumber,
  PlayerColumn,
  PlayerSort,
  PLAYER_INFO_COLUMN_WIDTH,
  PLAYER_LOGO_COLUMN_WIDTH,
  PLAYER_METRIC_COLUMN_WIDTH,
  PLAYER_SIDE_PADDING,
} from "../../utils/playerColumns";
// @ts-ignore
import { Spinner } from "../Spinner/Spinner.tsx";

interface PlayerRow {
  id?: number;
  name?: string;
  number?: number | string;
  params?: Record<string, unknown>[];
}

interface TeamPlayers {
  id: number;
  name: string;
  players_stat: PlayerRow[];
}

interface TableMainProps {
  players: TeamPlayers;
  columns: PlayerColumn[];
  sort: PlayerSort | null;
  lastChild?: boolean;
  color?: string;
  logo?: string;
}

export default function TableMain({
  lastChild,
  players,
  columns,
  sort,
  color,
  logo,
}: TableMainProps) {
  const [isLoad, setIsLoad] = useState(true);
  const visibleColumns = useMemo(
    () => columns.filter((column) => column.isShow),
    [columns]
  );
  const metricsMinWidth = useMemo(
    () => getMetricsMinWidth(visibleColumns),
    [visibleColumns]
  );
  const metricsTemplateColumns = useMemo(
    () =>
      visibleColumns.length > 0
        ? `repeat(${visibleColumns.length}, ${PLAYER_METRIC_COLUMN_WIDTH}px)`
        : "none",
    [visibleColumns.length]
  );

  const playersArr = useMemo(() => {
    const playersStat = Array.isArray(players.players_stat) ? players.players_stat : [];

    if (!sort) {
      return playersStat;
    }

    return [...playersStat].sort((a, b) => {
      const aValue = getPlayerMetricNumber(a?.params?.[0], sort.columnId);
      const bValue = getPlayerMetricNumber(b?.params?.[0], sort.columnId);

      if (sort.direction === "asc") {
        return aValue - bValue;
      }
      return bValue - aValue;
    });
  }, [players.players_stat, sort]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setIsLoad(false);
    }, 1300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div
      className="grid min-w-full w-max"
      style={{ gridTemplateColumns: `${PLAYER_LOGO_COLUMN_WIDTH}px auto` }}
    >
      <div className="flex flex-col sticky left-0 z-30">
        <div
          style={{ backgroundColor: color }}
          className={`${lastChild ? "rounded-bl-[10px]" : ""} h-full ${
            playersArr.length > 0 && !isLoad ? "" : "min-h-[30em]"
          } relative`}
        >
          <img className="w-full" src={logo} alt="" />
        </div>
      </div>
      {isLoad ? (
        <Spinner />
      ) : (
        <div className="h-full dark:bg-[#1B2436] relative">
          {playersArr.length > 0 ? (
            <ul>
              {playersArr.map(({ id, name, number, params }, index) => (
                <PlayerItem
                  key={`${id || "player"}-${name || "name"}-${index}`}
                  name={name}
                  number={number}
                  params={params}
                  columns={visibleColumns}
                  metricsMinWidth={metricsMinWidth}
                  metricsTemplateColumns={metricsTemplateColumns}
                />
              ))}
            </ul>
          ) : (
            <p className="text-[24px] text-center font-[500] absolute left-[50%] top-[50%] -translate-y-2/4 -translate-x-2/4">
              The line-ups will be updated shortly
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface PlayerItemProps {
  name?: string;
  number?: number | string;
  params?: Record<string, unknown>[];
  columns: PlayerColumn[];
  metricsMinWidth: number;
  metricsTemplateColumns: string;
}

const PlayerItem = ({
  name,
  number,
  params,
  columns,
  metricsMinWidth,
  metricsTemplateColumns,
}: PlayerItemProps) => {
  const playerParams = params?.[0];

  return (
    <li
      className="grid py-[2px] border-b border-[#6886BD] dark:text-white min-w-full w-max"
      style={{ gridTemplateColumns: `${PLAYER_INFO_COLUMN_WIDTH}px auto` }}
    >
      <div
        className="sticky z-20 bg-[#EBF1FF] dark:bg-[#1B2436] flex gap-[25px] overflow-hidden"
        style={{
          left: `${PLAYER_LOGO_COLUMN_WIDTH}px`,
          paddingLeft: `${PLAYER_SIDE_PADDING}px`,
          paddingRight: `${PLAYER_SIDE_PADDING}px`,
        }}
      >
        <div className="font-[600] min-w-[20px]">{number ?? "-"}</div>
        <div className="flex items-center gap-[5px] w-full">{name || ""}</div>
      </div>
      <ul
        className="grid w-max"
        style={{
          minWidth: `${metricsMinWidth}px`,
          gridTemplateColumns: metricsTemplateColumns,
          paddingRight: `${PLAYER_SIDE_PADDING}px`,
        }}
      >
        {columns.map((column) => {
          const metricValue = getPlayerMetricNumber(playerParams, column.id);

          return (
            <li
              className="flex justify-center items-center px-[2px] text-center"
              key={column.id}
            >
              {Math.round(metricValue)}
            </li>
          );
        })}
      </ul>
    </li>
  );
};
